import { getPOGs, getPrivatePOGs, createPOG, deletePOG } from "@/api/map";
import { transferPog } from "@/api/users";
import Swal from "sweetalert2";

// initial state
const state = {
  mapbox_gl_token:
    "pk.eyJ1IjoiZGNhaXJucyIsImEiOiJjanM4YmZmb3kxYTVxM3l0ZnJiNmJ4cXkwIn0.O6iCF0hbunCynqLJGMftuQ",
  dataLoaded: false,
  pogs: [],

  // Base Maps
  baseMaps: [
    {
      name: "Streets",
      value: "mapbox://styles/mapbox/streets-v11",
    },
    {
      name: "Satellite",
      value: "mapbox://styles/mapbox/satellite-streets-v11",
    },
    {
      name: "Outdoors",
      value: "mapbox://styles/mapbox/outdoors-v11",
    },
    {
      name: "Light",
      value: "mapbox://styles/mapbox/light-v10",
    },
    {
      name: "Dark",
      value: "mapbox://styles/mapbox/dark-v10",
    },
  ],

  // Base Layers
  baseLayers: [
    {
      name: "Watersheds",
      url: null,
      toggled: true,
    },
    {
      name: "Stream Names",
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/streams/{z}/{x}/{y}.pbf",
      sourceLayer: "alberta_streamsgeojsonl",
      toggled: true,
      labels: true,
      labelField: "NAME_1",
      noGeo: true,
      labelLayout: { "text-size": 10 },
      labelPaint: { "text-color": "#222c5c" },
    },
    {
      name: "Streams",
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/streams/{z}/{x}/{y}.pbf",
      toggled: false,
      labels: false,
      sourceLayer: "alberta_streamsgeojsonl",
      type: "line",
      paint: {
        "line-color": "#222c5c",
        "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.1, 13, 1.5],
      },
    },
    {
      name: "Cities",
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/cities/{z}/{x}/{y}.pbf",
      toggled: false,
      labels: false,
      sourceLayer: "yamkjljz",
      type: "fill",
      paint: {
        "fill-opacity": 0.4,
        "fill-outline-color": "rgba(0, 0, 0, 0)",
        "fill-color": "rgba(214, 62, 149, 1)",
      },
    },
    {
      name: "Municipal Boundaries",
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/municipalities/{z}/{x}/{y}.pbf",
      toggled: false,
      labels: true,
      labelField: "NAME",
      sourceLayer: "municipalities",
      type: "line",
      paint: {
        "line-color": "#514f52",
        "line-width": 2,
      },
    },
  ],

  watershedCorrelation: {
    "STURGEON RIVER": "Sturgeon",
    "BUCK LAKE - WABAMUM LAKE": "Modeste",
    "WHITEMUD/BLACKMUD CREEKS": "Strawberry",
    "VERMILION RIVER": "Vermilion",
    Modeste: "Modeste",
    Strawberry: "Strawberry",
    Vermilion: "Vermilion",
    "CHIP LAKE - PADDLE RIVER": "Mid-Pembina",
  },

  // Riparian Layers
  // The color pallette is designed for 3, 4, or 8 options
  // In the case of 8; 1, 3, 5, and 7 will be used
  riparianLayers: [
    {
      name: "Intactness",
      tileID: "Intactness",
      field_options: ["Intactness"],
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/riparian/intactness/{z}/{x}/{y}.pbf",
      layer: "intactness",
      type: "fill",
      layout: {},
      paint: {
        "fill-opacity": 0.5,
      },
      public: true,
      options: [
        "Very Low",
        "Very Low Intactness",
        "Low",
        "Low Intactness",
        "Moderate",
        "Moderate Intactness",
        "High",
        "High Intactness",
      ],
    },
    {
      name: "Prioritization",
      tileID: "Prioritization",
      field_options: ["Prioritization"],
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/riparian/prioritization/{z}/{x}/{y}.pbf",
      layer: "prioritization",
      type: "fill",
      layout: {},
      paint: {
        "fill-opacity": 0.4,
      },
      public: false,
      options: [
        "High Restoration Priority",
        "Moderate Restoration Priority",
        "Moderate Conservation Priority",
        "High Conservation Priority",
      ],
    },
    {
      name: "Catchment Pressure",
      tileID: "CatchmentPressure",
      field_options: ["Pressure"],
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/riparian/catchment_pressure/{z}/{x}/{y}.pbf",
      layer: "catchment_pressure",
      type: "fill",
      layout: {},
      paint: {
        "fill-opacity": 0.4,
      },
      public: false,
      options: ["High", "Moderate", "Low"],
    },
  ],

  // Spatial Stats Tool
  spatialStatsWatersheds: [
    {
      name: "HUC 2",
      mapID: "huc2",
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/watersheds/huc2/{z}/{x}/{y}.pbf",
      layer: "huc2",
      paint: {
        "fill-opacity": 0.4,
      },
      nameField: "NAME",
    },
    {
      name: "HUC 6",
      mapID: "huc6",
      url:
        "https://nswa-hydro-data.s3-us-west-2.amazonaws.com/vector_tiles/watersheds/huc6/{z}/{x}/{y}.pbf",
      layer: "huc6",
      paint: {
        "fill-opacity": 0.4,
      },
      nameField: "NAME",
    },
    {
      name: "Assessed Waterbodies",
      mapID: "assessedWaterbodies",
      url:
        "https://nswa-hydro-data.s3.us-west-2.amazonaws.com/spatial_stats/assessed_waterbodies/{z}/{x}/{y}.pbf",
      layer: "assessed_waterbodies",
      paint: {
        "fill-opacity": 0.4,
      },
      nameField: "NAME",
    },
  ],
};

// getters
const getters = {};

// actions
const actions = {
  getPublicData({ commit }) {
    commit("mapDataLoaded", false);
    getPOGs().then((pogs) => {
      commit("updatePOGs", pogs);
      commit("mapDataLoaded", true);
    });
  },
  getAllData({ commit, rootState }) {
    commit("mapDataLoaded", false);

    getPOGs().then((pogs) => {
      getPrivatePOGs().then((response) => {
        let allPogs = [...pogs, ...response].filter((pog) => {
          return (
            // Admins can see all
            rootState.settings.adminUsers.includes(
              rootState.settings.user.username
            ) ||
            // Legacy: no permissions included, visible to all logged in users
            pog.perms === undefined ||
            // Out of draft, admins or public can see
            ["auth", "public"].includes(pog.perms) ||
            // Users own pog
            pog.createdByUser === rootState.settings.user.username
          );
        });

        commit("updatePOGs", allPogs);
        commit("mapDataLoaded", true);
      });
    });
  },
  createPOG({ dispatch }, pog) {
    createPOG(pog)
      .then(() => {
        Swal.fire({
          timer: 850,
          icon: "success",
          showConfirmButton: false,
        });
        dispatch("getAllData");
      })
      .catch((err) => {
        console.error(err); //eslint-disable-line no-console
      });
  },
  deletePOG({ dispatch }, pogID) {
    deletePOG(pogID)
      .then(() => {
        dispatch("getAllData");
      })
      .catch((err) => {
        console.error(err); //eslint-disable-line no-console
      });
  },
  async transferPOG({ dispatch, commit }, payload) {
    commit("mapDataLoaded", false);
    const pogID = payload.pogID;
    const sourceUser = payload.sourceUser;
    const targetUser = payload.targetUser;
    transferPog(pogID, sourceUser, targetUser)
      .then(() => {
        Swal.fire({
          timer: 850,
          icon: "success",
          showConfirmButton: false,
        });
        dispatch("getAllData");
      })
      .catch((err) => {
        Swal.fire({
          text: err,
          icon: "error",
          showConfirmButton: true,
        });
      })
      .finally(() => commit("mapDataLoaded", true));
  },
};

// mutations
const mutations = {
  updatePOGs(state, data) {
    state.pogs = data;
  },
  clearPrivateData(state) {
    state.pogs = null;
  },
  mapDataLoaded(state, value) {
    state.dataLoaded = value;
  },
  toggleBaseLayer(state, name) {
    state.baseLayers.forEach((layer) => {
      if (layer.name === name) {
        layer.toggled = !layer.toggled;
      }
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
